import React, { useEffect, useState } from 'react'
import * as Sentry from '@sentry/nextjs'
import differenceInDays from 'date-fns/differenceInDays'
import add from 'date-fns/add'
import { SubmitHandler, useForm } from 'react-hook-form'
import { HeadSection } from '../components/HeadSection'
import { LogoSection } from '../components/LogoSection'
import { MainSection } from '../components/MainSection'
import { FormDescription, FormError, FormHeading, FormSubmit, IFormInput } from '../components/Form'
import { Input } from '../components/Form/Input'
import { FileInput } from '../components/Form/FileInput'
import { searchProductBySku } from '../lib/data/assetExplorer'
import { makeDate, makeUuid, submitS3ImageUpload, sendEmail } from '../lib/utils/functions'
import { iRegistration } from '../lib/types/prisma'
import { useRouter } from 'next/router'

export default function Registration() {
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IFormInput>()
  const [warrantyPeriod, setWarrantyPeriod] = useState<number | undefined>(undefined)
  const [contactSupportError, setContactSupportError] = useState<boolean>(false)
  const [outOfWarranty, setOutOfWarranty] = useState<boolean>(false)
  const [isFetchingProductData, setIsFetchingProductData] = useState<boolean>(false)

  // Watch the item sku field
  const itemNumberValue = watch('pipedreamItemNumber')
  const router = useRouter()

  const registerProduct = async (
    formInput: IFormInput,
    warrantyConfirmationNumber: string,
    registrationId: string,
    extension: string
  ) => {
    const axios = require('axios')
    const datePurchased = makeDate(formInput.purchaseDate)
    const warrantyEndDate = add(datePurchased, {
      days: warrantyPeriod,
    })

    const data: iRegistration = {
      warrantyConfirmationNumber: warrantyConfirmationNumber,
      registrationId: registrationId,
      productName: formInput.productName,
      productNumber: formInput.pipedreamItemNumber,
      firstName: formInput.firstName,
      lastName: formInput.lastName,
      email: formInput.email,
      city: formInput.city,
      state: formInput.state,
      zip: formInput.zip,
      datePurchased,
      warrantyCreatedAt: new Date(),
      warrantyEndDate,
      address: formInput.address,
      newsletterOptin: formInput.newsletterOptin,
      fileType: extension,
    }

    // Post to internal endpoint for saving
    const result = await axios({
      method: 'POST',
      url: '/api/prisma/saveRegistration',
      data,
    })

    // Show confirmation screens or send emails based on this result?
    if (result.status !== 200) router.push('/errorRegistration')
    router.push({
      pathname: '/successRegistration',
      query: { data: JSON.stringify(result) },
    })
  }

  const onSubmit: SubmitHandler<IFormInput> = async data => {
    // const [serviceId, userTemplateId, userId] = [
    //   'service_s5usay1',
    //   'template_7209nis',
    //   'user_eH22lGwqsG97Q9atyNFC3',
    // ]
    const formData = '#registrationForm'
    const fileList: FileList = data.proofOfPurchase
    const file: File = fileList[0]
    const extension = file.name.split('.').pop()

    const warrantyConfirmationNumber = `WC_${makeUuid()}`
    setValue('warrantyConfirmationNumber', warrantyConfirmationNumber)
    const registrationId = `RID_${makeUuid()}`
    setValue(
      'proofOfPurchaseLink',
      `https://product-warranties.s3.us-west-1.amazonaws.com/${warrantyConfirmationNumber}.${extension}`
    )
    const now = new Date()
    const purchaseDate = makeDate(data.purchaseDate)
    const dayDiff = differenceInDays(now, purchaseDate)
    const stillUnderWarranty = warrantyPeriod - dayDiff > 0
    setOutOfWarranty(!stillUnderWarranty)

    if (stillUnderWarranty) {
      await submitS3ImageUpload(file, 'proofOfPurchase', warrantyConfirmationNumber)
      registerProduct(data, warrantyConfirmationNumber, registrationId, extension)
      sendEmail(
        process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID,
        process.env.NEXT_PUBLIC_EMAILJS_REGISTRATION_CONFIRMATION_TEMPLATE,
        formData,
        process.env.NEXT_PUBLIC_EMAILJS_USER_ID
      )
    }
  }

  /**
   * Get product data by itemNumberValue aka SKU
   * - also sets warranty period and product name field
   * @param itemNumberValue
   */
  const getProductData = async (itemNumberValue: string) => {
    const result = await searchProductBySku(itemNumberValue)
    const hasWarrantyPeriod = !!result?.data.meta?.warrantyPeriod
    if (!hasWarrantyPeriod) {
      Sentry.addBreadcrumb({
        category: 'getProductData',
        message: `Failed to find warranty period`,
        level: Sentry.Severity.Warning,
      })
      Sentry.setContext('getProductData:noWarrantyPeriod', {
        itemNumberValue,
        ...result?.data,
      })
      Sentry.captureException(new Error(`Product has no warranty period`))
    }
    return result?.data
  }

  const setFormStateBasedOnProduct = data => {
    if (!data) return
    data.product ? setValue('productName', data.product.title) : setValue('productName', '')
    console.log(data.meta)
    const hasWarrantyPeriod = !!data.meta?.warrantyPeriod

    hasWarrantyPeriod && setWarrantyPeriod(parseInt(data.meta.warrantyPeriod.value))

    if (!hasWarrantyPeriod) {
      setContactSupportError(true)
    } else {
      setContactSupportError(false)
    }
  }

  /**
   * Listen to when the item number is updated
   */
  useEffect(() => {
    if (!itemNumberValue) return
    setIsFetchingProductData(true)

    const productData = getProductData(itemNumberValue).then(result => {
      setIsFetchingProductData(false)
      setFormStateBasedOnProduct(result)
    })
  }, [itemNumberValue])

  useEffect(() => {}, [outOfWarranty])

  return (
    <>
      <HeadSection title='Pipedream Product Registration' metaDescription='Product Registration' />
      <MainSection>
        <LogoSection />
        <form id='registrationForm' onSubmit={handleSubmit(onSubmit)} encType='multipart/form-data'>
          <FormHeading>Product Registration</FormHeading>
          <FormDescription>
            Thank you for your choosing our products! Please register your product below to ensure
            your purchase is protected by the warranty and to save your proof of purchase in case of
            a future claim. This warranty is valid from the date of purchase against mechanical
            failure or manufacturer defect. Claiming on the warranty requires proof of purchase from
            a Pipedream Products Authorized Retailer within the warranty.
          </FormDescription>

          {outOfWarranty && (
            <FormError> Warranty period expired for this item #{itemNumberValue}</FormError>
          )}

          {contactSupportError && (
            <FormError>
              Please contact customer service at 800-421-5030 or
              customerservice@pipedreamproducts.com
            </FormError>
          )}
          <div className='main-container'>
            <div className='input-container'>
              <Input
                fieldName='pipedreamItemNumber'
                label='Pipedream Item #'
                register={register}
                registerOptions={{
                  required: 'Pipedream Item # is required',
                  maxLength: {
                    value: 20,
                    message: 'Pipedream Item # can not be more than 20 characters',
                  },
                  pattern: {
                    value: /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/,
                    message: 'Letters and numbers only',
                  },
                }}
                errors={errors.pipedreamItemNumber}
              />
            </div>
            <div className='input-container'>
              <Input
                disabled
                fieldName='productName'
                label='Product Name'
                register={register}
                registerOptions={{
                  required: false,
                }}
                errors={errors.productName}
                isLoading={isFetchingProductData}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='purchaseDate'
                type='date'
                label='Purchase Date'
                register={register}
                registerOptions={{
                  required: 'Please tell us when you purchased your product',
                }}
                errors={errors.purchaseDate}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='email'
                label='Email Address'
                register={register}
                registerOptions={{
                  required: 'Email is required',
                  pattern: {
                    value:
                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: 'Please enter a valid email address',
                  },
                }}
                errors={errors.email}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='firstName'
                label='First Name'
                register={register}
                registerOptions={{
                  required: 'First Name is required',
                  maxLength: {
                    value: 20,
                    message: 'First Name can not be more than 20 characters',
                  },
                  pattern: {
                    value: /^[A-Za-z]+$/i,
                    message: 'Alphabetical characters only',
                  },
                }}
                errors={errors.firstName}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='lastName'
                label='Last Name'
                register={register}
                registerOptions={{
                  required: 'Last Name is required',
                  maxLength: {
                    value: 20,
                    message: 'Last Name can not be more than 20 characters',
                  },
                  pattern: {
                    value: /^[A-Za-z]+$/i,
                    message: 'Alphabetical characters only',
                  },
                }}
                errors={errors.lastName}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='address'
                label='Street Address'
                register={register}
                registerOptions={{
                  required: 'Address is required',
                  maxLength: {
                    value: 75,
                    message: 'Address can not be more than 20 characters',
                  },
                  pattern: {
                    value: /^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/,
                    message: 'Alphabetical characters only',
                  },
                }}
                errors={errors.address}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='city'
                label='City'
                register={register}
                registerOptions={{
                  required: 'City is required',
                  maxLength: {
                    value: 20,
                    message: 'City can not be more than 20 characters',
                  },
                  pattern: {
                    value: /^[A-Za-z]+$/i,
                    message: 'Alphabetical characters only',
                  },
                }}
                errors={errors.city}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='state'
                label='State'
                register={register}
                registerOptions={{
                  required: 'State is required',
                  maxLength: {
                    value: 20,
                    message: 'State can not be more than 20 characters',
                  },
                  pattern: {
                    value: /^[A-Za-z]+$/i,
                    message: 'Alphabetical characters only',
                  },
                }}
                errors={errors.state}
              />
            </div>
            <div className='input-container'>
              <Input
                fieldName='zip'
                label='Zip Code'
                register={register}
                registerOptions={{
                  required: 'Zip Code is required',
                  maxLength: {
                    value: 5,
                    message: 'Zip Code can not be more than 5 characters',
                  },
                  pattern: { value: /^[0-9]+$/i, message: 'Numbers only' },
                }}
                errors={errors.zip}
              />
            </div>
            <div className='input-container'>
              <Input
                readOnly
                fieldName='newsletterOptin'
                type='checkbox'
                label='Would you like to receive our newsletter?'
                register={register}
                registerOptions={{
                  required: false,
                }}
              />
            </div>
            <div className='input-container'>
              <FileInput
                fieldName='proofOfPurchase'
                label='Proof Of Purchase(Max Size 2MB)'
                register={register}
                registerOptions={{
                  required: 'Please provide proof of purchase',
                }}
                errors={errors.proofOfPurchase}
              />
            </div>
          </div>

          <Input
            type='hidden'
            fieldName='warrantyConfirmationNumber'
            label=''
            register={register}
            registerOptions={{
              required: false,
            }}
          />
          <Input
            type='hidden'
            fieldName='proofOfPurchaseLink'
            label=''
            register={register}
            registerOptions={{
              required: false,
            }}
          />

          <FormSubmit disabled={contactSupportError} className='submit-button' type='submit' />
        </form>
      </MainSection>
    </>
  )
}
